/**
 *  @class
 *  @function NewsletterPopup
 *
 *  The pop-up's controller (sections/overlay-popup.liquid). The dialog is
 *  the section's <modal-dialog> (modal-dialog.js), which moves itself to
 *  <body> and has data-delay="0", so it never opens on its own: this
 *  element, its sibling in the section, decides when it does.
 *
 *  Triggers: a delay (data-delay, in seconds; 0 turns it off), a scroll
 *  depth (data-scroll, the percentage of the page's scrollable height) and
 *  exit intent (data-exit-intent: the pointer leaving through the top of
 *  the window, on devices whose pointer is fine and hovers). The first to
 *  fire opens the pop-up and the others stop.
 *
 *  Courtesy: it never opens over a drawer, another modal or the age gate.
 *  It waits until they close (panel:closed, or a modal's open attribute),
 *  then opens.
 *
 *  Memory: every close counts, however it happens (the close button, the
 *  backdrop, Escape), and so does a submit. After one the pop-up stays
 *  away (data-frequency) for the session (sessionStorage), for data-days
 *  days or for good (localStorage). A signup keeps it away for good. The
 *  record is saved under the modal's id, where modal-dialog.js used to
 *  save the date of a close: that date counts as a close. Storage can be
 *  missing or refused (private modes), so every access is guarded, and the
 *  pop-up then has no memory. Nothing is saved in the theme editor.
 *
 *  Signup: the form (its id is data-form) posts the usual way. Shopify
 *  returns to the page with ?customer_posted=true and the form's id as the
 *  hash; the section then renders the success view (heading, text and the
 *  discount code with a Copy button), and the pop-up opens on it at once.
 *  Returned with the form's errors, it opens on the form. Every customer
 *  form on a returned page reads as posted: when another form on the page
 *  was the one sent, the pop-up drops its success view and its form's
 *  notices.
 *
 *  Teaser (.popup-teaser, optional): after a close, a tab reopens the
 *  pop-up until a signup. Its own button dismisses it until the pop-up is
 *  closed again.
 *
 *  Theme editor: modal-dialog.js opens the pop-up while its section is
 *  selected. No trigger runs and nothing is saved; the teaser stays in
 *  view so it can be styled.
 */
if (!customElements.get('newsletter-popup')) {
  const DAY = 24 * 60 * 60 * 1000;

  // theme.strings (layout/theme.liquid), with English when it's missing.
  const popupString = (name, fallback) => (window.theme && window.theme.strings && window.theme.strings[name]) || fallback;

  // window.localStorage itself throws where storage is blocked.
  const getStore = (type) => {
    try {
      return window[type] || null;
    } catch (e) {
      return null;
    }
  };

  // The record under key: { at, teaser, signup }. modal-dialog.js saved
  // only the date of a close, as a JSON string.
  const readRecord = (store, key) => {
    let value = null;
    try {
      value = store ? JSON.parse(store.getItem(key)) : null;
    } catch (e) {
      return null;
    }
    if (typeof value === 'string') {
      const at = Date.parse(value);
      return Number.isNaN(at) ? null : { at };
    }
    return value && typeof value === 'object' ? value : null;
  };

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      customElements.whenDefined('modal-dialog').then(() => this.init());
    }

    disconnectedCallback() {
      this.started = false;
      this.disarm();
      this.unwait();
      clearTimeout(this.copiedTimer);
      if (this.observer) this.observer.disconnect();
      if (this.listening) this.listening.abort();
      this.modal = null;
    }

    init() {
      if (!this.isConnected || this.modal) return;
      this.modal = document.getElementById(this.dataset.modal);
      if (!this.modal) return;
      this.dialog = this.modal.querySelector('[role="dialog"]');
      this.teaser = this.querySelector('.popup-teaser');
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.key = this.modal.id;
      this.frequency = this.dataset.frequency || 'days';
      this.days = parseInt(this.dataset.days, 10) || 14;

      // Every close, however it happens: hide() removes [open].
      this.wasOpen = this.modal.hasAttribute('open');
      this.observer = new MutationObserver(() => this.onToggle());
      this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });

      // The listeners on the modal and the teaser go with this element: in
      // the theme editor the modal stays in the section, so a section moved
      // in the page (disconnected, then connected again) would otherwise
      // bind them a second time.
      this.listening = new AbortController();
      const signal = this.listening.signal;

      // The page leaves with the post: until Shopify's answer is back (and
      // if a spam check page loses the hash), the pop-up stays closed.
      this.modal.addEventListener('submit', () => this.rememberClose(), { signal });

      const copy = this.modal.querySelector('.popup-modal__copy');
      if (copy) copy.addEventListener('click', () => this.copyCode(), { signal });

      if (this.teaser) {
        const open = this.teaser.querySelector('.popup-teaser__open');
        open.addEventListener('click', () => this.modal.show(open), { signal });
        this.teaser.querySelector('.popup-teaser__dismiss').addEventListener('click', () => {
          this.teaser.hidden = true;
          this.save(this.frequency === 'session' ? 'sessionStorage' : 'localStorage', { teaser: true });
        }, { signal });
      }

      if (this.designMode) {
        if (this.teaser) this.teaser.hidden = false;
        return;
      }

      const returned = this.returned();
      if (returned === 'success') {
        this.save('localStorage', { signup: true });
        this.showSuccess();
        this.tryOpen();
        // Shown once: without the hash, a reload or a return to this page
        // through the history doesn't open the success view (and its code)
        // again. The query stays, so the page's other forms keep their
        // notices.
        try {
          window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
        } catch (e) {
          // No history API (a sandboxed frame): it opens again on a reload.
        }
        return;
      }
      if (returned === 'error') {
        this.tryOpen();
        return;
      }

      const state = this.state();
      if (state.signup) return;
      if (state.closed) {
        if (this.teaser && !state.teaser) this.teaser.hidden = false;
        return;
      }
      this.arm();
    }

    // Back from this form's post: 'success' or 'error' (the form shows its
    // errors). Back from another form's, this one's notices are dropped.
    returned() {
      const formId = this.dataset.form;
      if (!formId) return null;
      let hash = '';
      try {
        hash = decodeURIComponent(window.location.hash.slice(1));
      } catch (e) {
        return null;
      }
      const success = this.modal.querySelector('.popup-modal__success');
      if (hash === formId) {
        if (success && new URLSearchParams(window.location.search).get('customer_posted') === 'true') return 'success';
        return this.modal.querySelector('.form-notification.error') ? 'error' : null;
      }
      if (hash && document.getElementById(hash)) {
        if (success) success.remove();
        this.modal.querySelectorAll('.signup-container .form-notification').forEach((notice) => {
          const field = this.modal.querySelector(`[aria-describedby="${notice.id}"]`);
          if (field) {
            field.removeAttribute('aria-describedby');
            field.removeAttribute('aria-invalid');
          }
          notice.remove();
        });
      }
      return null;
    }

    // What the visitor did before: signed up, closed it within the
    // frequency's time, and dismissed the teaser since that close.
    state() {
      const local = readRecord(getStore('localStorage'), this.key) || {};
      const record = (this.frequency === 'session' ? readRecord(getStore('sessionStorage'), this.key) : local) || {};
      let closed = false;
      if (record.at) {
        closed = this.frequency !== 'days' || Date.now() - record.at < this.days * DAY;
      }
      return { signup: !!local.signup, closed, teaser: !!record.teaser };
    }

    save(type, patch) {
      if (this.designMode) return;
      const store = getStore(type);
      if (!store) return;
      try {
        store.setItem(this.key, JSON.stringify(Object.assign(readRecord(store, this.key) || {}, patch)));
      } catch (e) {
        // Refused (private mode, full): no memory this visit.
      }
    }

    // A new close brings the teaser back.
    rememberClose() {
      this.save(this.frequency === 'session' ? 'sessionStorage' : 'localStorage', { at: Date.now(), teaser: false });
    }

    onToggle() {
      const open = this.modal.hasAttribute('open');
      if (open === this.wasOpen) return;
      this.wasOpen = open;
      if (open) {
        // Opened by a trigger, the teaser or the editor: the rest stop.
        this.disarm();
        this.unwait();
        return;
      }
      // After a signup there is nothing left to offer.
      if (this.designMode || this.success) return;
      this.rememberClose();
      if (this.teaser) this.teaser.hidden = false;
    }

    arm() {
      const delay = parseInt(this.dataset.delay, 10) || 0;
      const depth = parseInt(this.dataset.scroll, 10) || 0;
      if (delay > 0) {
        this.timer = setTimeout(() => this.fire(), delay * 1000);
      }
      if (depth > 0) {
        // Measured once a frame at most; a page too short to scroll never
        // reaches it.
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          window.requestAnimationFrame(() => {
            this.ticking = false;
            const room = document.documentElement.scrollHeight - window.innerHeight;
            if (room > 0 && (window.scrollY / room) * 100 >= depth) this.fire();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      if (this.hasAttribute('data-exit-intent') && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.onMouseOut = (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.fire();
        };
        document.addEventListener('mouseout', this.onMouseOut);
      }
    }

    disarm() {
      clearTimeout(this.timer);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onMouseOut) document.removeEventListener('mouseout', this.onMouseOut);
      this.onScroll = null;
      this.onMouseOut = null;
    }

    fire() {
      this.disarm();
      this.tryOpen();
    }

    tryOpen() {
      if (!this.modal || this.modal.hasAttribute('open')) return;
      if (this.blocked()) {
        this.wait();
        return;
      }
      this.unwait();
      this.modal.show();
    }

    // A drawer or panel (OverlayManager), or any open modal: another
    // pop-up, a product page's pop-up or the age gate.
    blocked() {
      return (typeof OverlayManager !== 'undefined' && OverlayManager.hasOpen()) || !!document.querySelector('modal-dialog[open]');
    }

    wait() {
      if (this.retry) return;
      this.retry = () => this.tryOpen();
      document.addEventListener('panel:closed', this.retry);
      this.waitObserver = new MutationObserver(this.retry);
      this.waitObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    }

    unwait() {
      if (!this.retry) return;
      document.removeEventListener('panel:closed', this.retry);
      this.waitObserver.disconnect();
      this.retry = null;
    }

    // The success view takes the place of the blocks, and names the dialog.
    showSuccess() {
      this.success = this.modal.querySelector('.popup-modal__success');
      this.success.hidden = false;
      this.modal.classList.add('popup-modal--success');
      const heading = this.success.querySelector('.product-popup-modal--heading');
      if (heading && this.dialog) this.dialog.setAttribute('aria-labelledby', heading.id);
    }

    // The Clipboard API is missing on insecure origins and can be refused
    // (a frame without clipboard-write permission): then the code is
    // copied from a selected textarea. If that fails too, the code is
    // selected, for the visitor to copy.
    copyCode() {
      const value = this.modal.querySelector('.popup-modal__code-value');
      const code = value.textContent.trim();
      const write = navigator.clipboard && navigator.clipboard.writeText ?
        navigator.clipboard.writeText(code) :
        Promise.reject(new Error('Clipboard API unavailable'));

      write.catch(() => {
        if (!this.copyFromTextarea(code)) throw new Error('Copy to clipboard failed');
      }).then(() => this.showCopied(), () => {
        const range = document.createRange();
        range.selectNodeContents(value);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      });
    }

    // Inside the dialog, so its focus trap leaves it the focus.
    copyFromTextarea(text) {
      const active = document.activeElement,
        textarea = document.createElement('textarea');
      let copied = false;

      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.setAttribute('aria-hidden', 'true');
      textarea.tabIndex = -1;
      // 12pt keeps iOS from zooming in on the focused field.
      textarea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;font-size:12pt;opacity:0;pointer-events:none;';
      this.dialog.appendChild(textarea);
      textarea.focus({ preventScroll: true });
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      textarea.remove();
      if (active && active.focus) active.focus({ preventScroll: true });
      return copied;
    }

    // "Copied" in the status line for a few seconds. It's emptied first,
    // so a second copy is announced again.
    showCopied() {
      const status = this.modal.querySelector('.popup-modal__copied');
      if (!status) return;
      clearTimeout(this.copiedTimer);
      status.textContent = '';
      this.copiedTimer = setTimeout(() => {
        status.textContent = popupString('copied', 'Copied');
        this.copiedTimer = setTimeout(() => {
          status.textContent = '';
        }, 3000);
      }, 50);
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);
}
